import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Div from 'components/Div'
import Flex from 'components/Flex'
import { P } from 'components/Typo'
import IconSuccess from 'components/Icons/IconSuccess'
import IconInformation from 'components/Icons/IconInformation'
import IconWarning from 'components/Icons/IconWarning'
import IconError from 'components/Icons/IconError'
import {
  states,
  stateThemes,
  stateColors,
  paddingSizes,
  borderRadiusSizes,
  marginSizes
} from 'theme/default'

export const notificationStates = states
export const notificationTheme = stateThemes

const getColor = (props) =>
  props.lightTheme
    ? stateColors.colors[props.state][stateThemes.light]
    : stateColors.colors[props.state][stateThemes.dark]

const getBackgroundColor = (props) =>
  props.lightTheme
    ? stateColors.backgroundColors[props.state][stateThemes.light]
    : stateColors.backgroundColors[props.state][stateThemes.dark]

export const getIcon = ({ state }) => {
  switch (state) {
    case states.success:
      return IconSuccess
    case states.informative:
      return IconInformation
    case states.warning:
      return IconWarning
    case states.error:
      return IconError
  }
}

const NotificationContainer = styled(Div).attrs(() => ({
  padding: paddingSizes.small,
  borderRadius: borderRadiusSizes.medium
}))`
  background-color: ${(props) => props.backgroundColor};
  color: ${(props) => props.color};
`
NotificationContainer.propTypes = {
  backgroundColor: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired
}

const IconContainer = styled(Flex).attrs(() => ({
  justifyContent: 'center',
  alignItems: 'center',
  marginRight: marginSizes.small
}))`
  height: 24px;
`

export const NotificationIcon = ({ Icon, iconSize, fillColor, className }) => (
  <IconContainer className={className}>
    <Icon fill={fillColor} iconSize={iconSize} />
  </IconContainer>
)
NotificationIcon.propTypes = {
  iconSize: PropTypes.string,
  fillColor: PropTypes.string.isRequired,
  Icon: PropTypes.func.isRequired
}

export const Notification = ({ children, ...props }) => {
  const color = getColor(props)
  const backgroundColor = getBackgroundColor(props)
  const Icon = getIcon(props)
  return children({ color, backgroundColor, Icon })
}
Notification.propTypes = {
  state: PropTypes.string,
  lightTheme: PropTypes.bool
}
Notification.defaultProps = {
  state: notificationStates.informative
}

// Note: if children is defined text and subText will be ignored
export const InlineNotification = ({
  className,
  state,
  lightTheme,
  children,
  text,
  subText,
  withIcon
}) => (
  <Notification state={state} lightTheme={lightTheme}>
    {({ color, backgroundColor, Icon }) => (
      <NotificationContainer
        className={className}
        color={color}
        backgroundColor={backgroundColor}
      >
        <Flex justifyContent='flex-start' alignItems='center'>
          {withIcon ? <NotificationIcon Icon={Icon} fillColor={color} /> : null}
          <Flex flexDirection='column'>
            {children || (
              <React.Fragment>
                <P color={color} normalised bold>
                  {text}
                </P>
                {subText && (
                  <P color={color} normalised>
                    {subText}
                  </P>
                )}
              </React.Fragment>
            )}
          </Flex>
        </Flex>
      </NotificationContainer>
    )}
  </Notification>
)
InlineNotification.propTypes = {
  ...Notification.propTypes,
  className: PropTypes.string,
  text: PropTypes.string,
  subText: PropTypes.string,
  children: PropTypes.any,
  withIcon: PropTypes.bool
}

InlineNotification.defaultProps = {
  withIcon: true
}
